import { Alert, Button, Result, Skeleton, Space } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTierCheck } from '../hooks/useCurrentTenant';
import type { SubscriptionTier } from '../types';

interface Props {
    requiredTier: SubscriptionTier;
    children: React.ReactNode;
    /** Custom feature name shown in the upgrade prompt; defaults to the tier name. */
    featureName?: string;
    /** Render this instead of the default upgrade panel when locked. */
    fallback?: React.ReactNode;
}

/**
 * Wrap a route or feature region so it shows an upgrade prompt instead of the
 * locked UI when the tenant is below {@code requiredTier}.
 *
 * Backend has the real gate (returns 402). This component is the UX layer —
 * keeps users from hitting an error message after clicking through; instead
 * they see the upgrade pitch up front.
 *
 * The backend gate is the source of truth; if this component is bypassed
 * (open the dev tools, swap React state, etc.) the API call still fails 402.
 */
export function SubscriptionGate({ requiredTier, children, featureName, fallback }: Props) {
    const { covered, loading, currentTier } = useTierCheck(requiredTier);
    const { t } = useTranslation();

    if (loading) {
        // Render a skeleton, not the locked UI — gives a sense of "still loading"
        // and avoids a flash of upgrade-prompt for a tenant who actually has the tier.
        return <Skeleton active paragraph={{ rows: 4 }} />;
    }

    if (covered) return <>{children}</>;
    if (fallback) return <>{fallback}</>;

    const feature = featureName || t(`subscription.tier.${requiredTier.toLowerCase()}`);

    return (
        <div style={{ padding: '40px 20px' }}>
            <Result
                icon={<LockOutlined style={{ color: '#1677ff' }} />}
                title={t('subscription.gate.title', { tier: requiredTier })}
                subTitle={t('subscription.gate.subtitle', { feature, currentTier: currentTier || 'STARTER' })}
                extra={
                    <Space>
                        <Link to="/settings/subscription">
                            <Button type="primary">{t('subscription.gate.upgradeBtn')}</Button>
                        </Link>
                        <Link to="/">
                            <Button>{t('subscription.gate.backHome')}</Button>
                        </Link>
                    </Space>
                }
            />
            <Alert
                type="info"
                showIcon
                style={{ maxWidth: 720, margin: '24px auto' }}
                message={t('subscription.gate.whyMessage')}
                description={t('subscription.gate.whyDescription')}
            />
        </div>
    );
}
